import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, finalize, tap } from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { IVaccinationDetailed } from '../interfaces/Vaccination/IVaccinationDetailed';
import { IVaccinationDropdown } from '../interfaces/Vaccination/IVaccinationDropdown';
import { IAddVaccination } from '../interfaces/Vaccination/IAddVaccination';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';

@Injectable({ providedIn: 'root' })
export class VaccinationService {
  public vaccinationModalSub = new BehaviorSubject<boolean>(false);

  public vaccinationEditSub = new BehaviorSubject<
    IVaccinationDetailed | undefined
  >(undefined);

  public vaccinationDataSub = new BehaviorSubject<IVaccinationDetailed[]>([]);

  public vaccinationLoadingSub = new BehaviorSubject<boolean>(false);
  public vaccinationLoadingSub$ = this.vaccinationLoadingSub.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  loadData(): Observable<IGenericServiceResponse<IVaccinationDetailed[]>> {
    this.vaccinationLoadingSub.next(true);
    return this.getVaccinations(
      this.patientService.patientIDsignal(),
      this.eocService.currentVisitId()
    ).pipe(
      tap((response) => {
        this.vaccinationDataSub.next(response?.data!);
      }),
      catchError((err) => {
        this.swalToastService.toastError('Unable to retrieve vaccinations');
        console.error('Error fetching vaccinations:', err);
        return EMPTY;
      }),
      finalize(() => this.vaccinationLoadingSub.next(false)),
    );
  }

  getData(): void {
    this.loadData().subscribe();
  }

  getVaccinations(
    patientId: string,
    visitId?: string
  ): Observable<IGenericServiceResponse<IVaccinationDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (visitId) {
      return this._http.get<IGenericServiceResponse<IVaccinationDetailed[]>>(
        `${environment.BACKEND_URL}Vaccination/${patientId}?visitId=${visitId}`,
        {
          headers: headers,
        }
      );
    } else {
      return this._http.get<IGenericServiceResponse<IVaccinationDetailed[]>>(
        `${environment.BACKEND_URL}Vaccination/${patientId}`,
        {
          headers: headers,
        }
      );
    }
  }

  getVaccinationDropdown(): Observable<
    IGenericServiceResponse<IVaccinationDropdown>
  > {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IVaccinationDropdown>>(
      `${environment.BACKEND_URL}Vaccination/DropdownData`,
      {
        headers: headers,
      }
    );
  }

  addVaccination(vaccinationObj: IAddVaccination) {
    vaccinationObj.visitId = this.eocService.currentVisitId();
    vaccinationObj.patientId = this.patientService.patientIDsignal();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post(
      `${environment.BACKEND_URL}Vaccination`,
      vaccinationObj,
      {
        headers: headers,
      }
    );
  }

  editVaccination(vaccinationObj: IAddVaccination) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put(
      `${environment.BACKEND_URL}Vaccination/${vaccinationObj.id}`,
      vaccinationObj,
      {
        headers: headers,
      }
    );
  }

  deleteVaccination(vaccinationId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}Vaccination/${vaccinationId}`,
      {
        headers: headers,
      }
    );
  }

  deleteVaccinationFromTable(vaccinationId: string) {
    // REMOVE THE Vaccination
    this.vaccinationDataSub.next(
      this.vaccinationDataSub.value.filter(
        (v) => v.id?.toString() != vaccinationId
      )
    );
  }
}
